
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { ConversionTracker } from "./ConversionTracker";
import { useEvangelismAssistant } from "./EvangelismAssistantProvider";
import { useToast } from "@/hooks/use-toast";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import { sendMessage } from "./services/evangelismService";
import ConversionComplete from "./ConversionComplete";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ChatInterfaceProps {
  worldview: string;
  onReset: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ worldview, onReset }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConversionComplete, setShowConversionComplete] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { 
    messages, 
    addMessage, 
    conversionStage, 
    setConversionStage, 
    resetChat,
    softeningScore,
    setSofteningScore
  } = useEvangelismAssistant();
  const { toast } = useToast();

  useEffect(() => {
    // Start the conversation with an AI message
    if (messages.length === 0) {
      const initialMessages: Record<string, string> = {
        atheist: "I don't see why anyone would believe in a god. There's just no evidence for it.",
        muslim: "I respect Jesus as a prophet, but Muhammad received the final revelation from Allah.",
        jew: "I believe in God, but I don't understand why Christians think Jesus was the Messiah.",
        hindu: "In my tradition, we believe there are many paths to God, not just one.",
        mormon: "I believe Jesus is the Son of God, but I also believe in the Book of Mormon.",
        jehovah: "Jesus is not God. He was the first creation of Jehovah.",
        blackhebrew: "The real Israelites are Black. The Bible was written about us, not white people.",
        newage: "I'm spiritual, but I don't believe in organized religion. I find my own truth.",
        universalist: "I believe everyone will be saved in the end. God is too loving to condemn anyone.",
        pluralist: "All religions are valid paths to the same divine reality. Christianity is just one way."
      };

      addMessage("ai", initialMessages[worldview] || "Hello, I'd like to talk about religion and spirituality. What do you believe?");
    }
  }, [worldview, messages.length, addMessage]);

  // Watch for conversion stage changes
  useEffect(() => {
    if (conversionStage === "converted" && messages.length > 2) {
      // Show the conversion complete dialog after a small delay
      const timer = setTimeout(() => {
        setShowConversionComplete(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [conversionStage, messages.length]);

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;
    
    addMessage("user", message);
    setIsLoading(true);
    setConnectionError(null);
    
    try {
      const result = await sendMessage(
        worldview, 
        message, 
        messages, 
        conversionStage, 
        softeningScore
      );
      
      // Update softening score if provided
      if (result.softening_score !== undefined) {
        setSofteningScore(result.softening_score);
      }
      
      // Update conversion stage based on response
      if (result.conversion_stage && result.conversion_stage !== conversionStage) {
        setConversionStage(result.conversion_stage);
        
        // Show toast notification when someone converts
        if (result.conversion_stage === "converted") {
          toast({
            title: "Praise God!",
            description: "You've led this soul to Christ! Continue to disciple them.",
            variant: "default",
          });
        }
      }
      
      // Add AI response to chat
      addMessage("ai", result.response);
    } catch (error) {
      console.error("Error fetching response:", error);
      
      // Set connection error message
      if (error instanceof Error) {
        setConnectionError(error.message);
      } else {
        setConnectionError("Something went wrong. Please try again.");
      }
      
      // Add a fallback message to the chat
      addMessage("ai", "I'm sorry, I'm having trouble responding right now. Can we try again?");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    resetChat();
    onReset();
  };

  const handleRetry = () => {
    setConnectionError(null);
    // Force a scroll refresh to make UI more responsive
    window.dispatchEvent(new Event('resize'));
  };

  return (
    <div className="flex flex-col space-y-4">
      <Button 
        variant="outline" 
        className="self-start mb-2 border-heaven-gold/50 text-heaven-gold hover:bg-heaven-gold/10"
        onClick={handleReset}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Choose a different worldview
      </Button>
      
      {connectionError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Connection Error</AlertTitle>
          <AlertDescription>
            {connectionError}
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-2 mt-2"
              onClick={handleRetry}
            >
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-3">
          <Card className="border-heaven-gold/20 flex flex-col h-[550px] w-full">
            <div className="flex-1 p-4 overflow-hidden">
              <MessageList isLoading={isLoading} />
            </div>
            <div className="border-t border-gray-200 w-full">
              <MessageInput 
                onSendMessage={handleSendMessage} 
                isLoading={isLoading} 
              />
            </div>
          </Card>
        </div>
        
        <div className="md:col-span-1">
          <ConversionTracker />
          <div className="mt-4">
            <img 
              src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//evange2.png" 
              alt="Evangelism guidance" 
              className="rounded-lg shadow-sm w-full"
            />
          </div>
        </div>
      </div>
      
      <div className="text-center text-sm text-gray-500 mt-4">
        <p>⚠️ Remember: This isn't a game. It's training for a spiritual battlefield.</p>
      </div>

      {/* Conversion complete dialog */}
      <ConversionComplete 
        open={showConversionComplete} 
        onOpenChange={setShowConversionComplete} 
      />
    </div>
  );
};

export default ChatInterface;
